/**
 * Script Composer → Toolbox chat hand-off (behind Flags.canUseToolbox()).
 *
 * Instead of the classic scene-generation pipeline, finishing the composer
 * stages the script and the user's choices as a chat message for the in-creator
 * agent to build from. Kept in its own module while feature-flagged; expected
 * to become the main flow.
 */
import { TOOL_NAMES } from 'js/src/agent/toolNames';

import { ApiService } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    saveDashboardSketchData,
    type SerializedDocument,
    type SerializedMedia,
} from 'js/src/components/dashboard/utils/dashboardSketchStorage';
import { SupportedFileType } from 'js/src/components/sketch/FileUpload';
import { getAIVoiceById } from 'js/src/data/ai-voices-and-avatars';
import {
    EDITOR_TAB_OPTIONS,
    getSelectedAspectRatio,
    getSelectedBlueprintId,
    getSelectedPaletteIndex,
    getSelectedTemplateId,
    getVideoIdOrThrow,
    getVideoInitType,
    setScriptComposerPlaceholderVideoId,
    setStage,
    setToolboxCreatingVideo,
    setVideoId,
    setVideoSlug,
    STAGES,
    switchEditorTabAction,
    VIDEO_INIT_TYPE,
} from 'js/src/data/app';
import { selectArticleId } from 'js/src/data/article/article-selectors';
import { getBlueprint } from 'js/src/data/blueprints';
import { getBrandKitById } from 'js/src/data/brand-kits';
import { selectDocumentName, selectDocumentUrl } from 'js/src/data/document';
import { setDocToScriptPage } from 'js/src/data/script-composer/script-composer-actions';
import {
    getCurrentAIAvatar,
    getDocToScriptImportType,
    getEditedScriptLanguage,
    getEnforceBlockSegmentation,
    getReferenceMaterial,
} from 'js/src/data/script-composer/script-composer-selectors';
import { getPaletteColorsAsHex, getTemplateColorPalettes } from 'js/src/data/templates';
import { getBrandKitId } from 'js/src/data/video';
import { requestChatHandoffConsume } from 'js/src/libs/chat-handoff-event-emitter';
import { SCRIPT_CREATION_SOURCES } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import uuid from 'js/src/libs/uuid';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';

export interface ToolboxHandoffMessageOptions {
    scriptLines: string[];
    /** Null leaves the narration choice to the agent (API runs that named no voice). */
    videoInitType: VIDEO_INIT_TYPE | null;
    language?: string | null;
    /** The AI voice the user picked. The slug is what the tools take. */
    voice?: { slug: string; name: string } | null;
    /** The AI avatar presenter the user picked. The id is what the tools take. */
    avatar?: { id: number; name: string } | null;
    templateId?: string | null;
    aspectRatio?: string | null;
    brandKit?: { id: number; name: string } | null;
    palette?: { name: string; colors: string[] } | null;
    blueprint?: { id: number; name: string } | null;
    /** True for "create one scene per script block"; false lets the AI split. */
    enforceBlockSegmentation?: boolean;
    referenceMaterial?: string | null;
    /** True when the source document is attached to the chat message instead. */
    hasAttachedSourceDocument?: boolean;
}

/**
 * Builds the message handed to the Toolbox chat agent. The agent has no
 * dedicated "use-this-script" tool, so the script and the user's choices are
 * encoded as natural-language instructions.
 *
 * The script sits at the top as plain text and everything else goes inside
 * <video-request> tags: the chat renderer strips XML-tagged sections from user
 * messages, so the user's message bubble shows exactly the script they wrote.
 * The request block repeats the script between markers because an attached
 * source document also lands above the block, and the agent can mistake it
 * for the script.
 */
export const buildToolboxHandoffMessage = ({
    scriptLines,
    videoInitType,
    language,
    voice,
    avatar,
    templateId,
    aspectRatio,
    brandKit,
    palette,
    blueprint,
    enforceBlockSegmentation,
    referenceMaterial,
    hasAttachedSourceDocument,
}: ToolboxHandoffMessageOptions): string => {
    const script = scriptLines
        .map((line) => line.trim())
        .filter(Boolean)
        .join('\n\n');

    const planDetails: string[] = [];
    if (videoInitType === null) {
        planDetails.push(
            '- Narration: your choice between an AI voiceover and text on media, whichever suits the script',
        );
    } else if (videoInitType === VIDEO_INIT_TYPE.TEXT_TO_AI_AVATAR) {
        planDetails.push('- Narration: AI avatar presenter');
    } else if (videoInitType === VIDEO_INIT_TYPE.TEXT_TO_AI_VOICEOVER) {
        planDetails.push('- Narration: AI voiceover');
    } else {
        planDetails.push('- Narration: text on media (no voiceover)');
    }
    if (avatar) {
        planDetails.push(
            `- Avatar presenter: "${avatar.name}" (id ${avatar.id}) — pass exactly this id as ` +
                `\`avatar_id\` to ${TOOL_NAMES.addAvatarSceneGroup}. Omitting it silently ` +
                'falls back to the default avatar, which is not what the user chose.',
        );
    }
    if (voice) {
        planDetails.push(
            `- Voice: "${voice.name}" (slug "${voice.slug}") — pass exactly this slug as ` +
                `\`voice_slug\`. The user already picked it, so do not look it up by name: ` +
                'the voice search is quality-gated and may not return it.',
        );
    }
    if (language) {
        planDetails.push(`- Language: ${language}`);
    }
    if (aspectRatio) {
        planDetails.push(`- Aspect ratio: ${aspectRatio}`);
    }
    if (brandKit) {
        planDetails.push(
            `- Brand kit: "${brandKit.name}" (id ${brandKit.id}) — apply it to the video with ` +
                `the ${TOOL_NAMES.applyBrandKit} tool.`,
        );
    } else if (palette?.colors.length) {
        planDetails.push(
            `- Color palette chosen by the user: "${palette.name}" (colors: ` +
                `${palette.colors.join(', ')}) — apply it to the video with the ` +
                `${TOOL_NAMES.setColorPalette} tool.`,
        );
    }
    if (blueprint) {
        planDetails.push(
            `- The user based this video on the "${blueprint.name}" blueprint (id ${blueprint.id}).`,
        );
    }
    if (enforceBlockSegmentation) {
        planDetails.push(
            '- Scene structure: create exactly one scene per script paragraph (the ' +
                'blank-line-separated blocks above), in order — do not merge or re-split them.',
        );
    }
    const referenceText = referenceMaterial?.trim();

    const lines: string[] = [script, '', '<video-request>'];

    if (templateId) {
        lines.push(
            `Create the video by calling ${TOOL_NAMES.createBlankVideo} with template_id "${templateId}"` +
                (aspectRatio ? ` and aspect_ratio "${aspectRatio}"` : '') +
                ' (use these exact values if available; otherwise pick the closest match).',
        );
    }

    lines.push(
        'The video script is between the SCRIPT START and SCRIPT END markers below. Build the ' +
            'video using that exact script, keeping the wording verbatim. Anything else in this ' +
            'message is not the script.',
        '=== SCRIPT START ===',
        script,
        '=== SCRIPT END ===',
    );

    if (hasAttachedSourceDocument) {
        lines.push(
            '',
            'The attached document is the source material the script was derived from. Use it ' +
                'only for context (e.g. finding relevant media). Do not use it as the script, and ' +
                'do not write a new script from it.',
        );
    }

    lines.push(
        '',
        'Do not ask the user how long the video should be — its duration is determined by the ' +
            'script.',
        '',
        'Plan:',
        ...planDetails,
    );

    if (referenceText) {
        lines.push(
            '',
            'The following is the source/reference material the script was derived from. ' +
                'Use it for context (e.g. finding relevant media) — it is NOT the script:',
            '<reference-material>',
            referenceText,
            '</reference-material>',
        );
    }

    lines.push('</video-request>');

    return lines.join('\n').trim();
};

const DOCUMENT_EXTENSION_TO_FILE_TYPE: Record<string, SupportedFileType> = {
    pdf: SupportedFileType.PDF,
    docx: SupportedFileType.DOCX,
    pptx: SupportedFileType.PPTX,
    txt: SupportedFileType.TXT,
    csv: SupportedFileType.CSV,
    tsv: SupportedFileType.TSV,
    xml: SupportedFileType.XML,
};

// The composer only stores the source document's name/url, not its mime type.
function inferDocumentFileType(fileName: string): SupportedFileType {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
    return DOCUMENT_EXTENSION_TO_FILE_TYPE[extension] ?? SupportedFileType.PDF;
}

/**
 * Collects the images the user attached in the composer as serialized media for
 * the hand-off; the chat re-hydrates them into the MediaRegistry by uuid.
 * Returns [] on failure — partial success beats blocking the hand-off.
 */
async function collectComposerImages(articleId: number | null): Promise<SerializedMedia[]> {
    if (articleId == null) {
        return [];
    }
    try {
        const response = await ApiService.articleImagesList({
            path: { article_pk: articleId },
        });
        const images = response.data ?? [];
        return images
            .filter((image): image is typeof image & { uuid: string } => !!image.uuid)
            .map((image, index) => ({
                id: image.uuid,
                name: `Attached image ${index + 1}`,
                type: SupportedFileType.PNG,
                uuid: image.uuid,
            }));
    } catch (error) {
        if (error instanceof Error) {
            ErrorReporter.capture(error);
        }
        return [];
    }
}

/**
 * Parks the current DB video as a placeholder and drops the user into the
 * toolbox chat's video-less build experience: wipes the video's scene groups,
 * unloads the video id (the parked DB video is adopted at turn end instead of
 * creating a second one — see createSingleVideoAtTurnEndOperation), switches to
 * the chat tab, and signals the chat to consume the staged hand-off message.
 * The message must already be staged (saveDashboardSketchData) before calling.
 */
export const parkVideoAndEnterChatOperation = createLuminaryOperation(
    async ({ luminary, dispatch, getState }) => {
        const videoId = getVideoIdOrThrow(getState());

        // Wipe the placeholder's default scenes so they don't survive into the
        // agent's build. Raw luminary API on purpose: the app-level delete
        // operation refuses to remove the last ordinary scene.
        [...luminary.sceneGroups].forEach((sceneGroup) => {
            luminary.removeSceneGroup(sceneGroup.id);
        });

        dispatch(setScriptComposerPlaceholderVideoId(videoId));
        // Mark the build in progress in the same batch that unloads the video
        // id, so the generating preview replaces the scene list in the very
        // render that drops the id — otherwise the scene list re-runs its
        // selectors against a null video id and crashes on getVideoIdOrThrow.
        dispatch(setToolboxCreatingVideo(true));
        dispatch(setVideoId({ videoId: null }));
        dispatch(setVideoSlug({ videoSlug: null }));
        dispatch(switchEditorTabAction({ nextTab: EDITOR_TAB_OPTIONS.CHAT }));
        dispatch(setDocToScriptPage({ page: null }));
        dispatch(setStage({ stage: STAGES.CREATOR }));

        // The chat consumes staged messages on mount, but here it is already
        // mounted — signal it to consume this one; consuming is idempotent.
        requestChatHandoffConsume();
    },
);

interface ToolboxHandoffArgs {
    scriptLines: string[];
    currentAIVoiceId: string | null;
    scriptWordCount: number;
}

/**
 * Finishes the script composer by handing off to the Toolbox chat: stages the
 * message (with the source doc/images as attachments), closes the composer, and
 * switches to the in-creator chat.
 *
 * The video created when the composer opened is parked as a placeholder and
 * unloaded, so the creator behaves exactly like the video-less toolbox build
 * (create_blank_video proceeds, controls stay hidden); the parked DB video is
 * adopted at turn end instead of creating a second one — see
 * createSingleVideoAtTurnEndOperation.
 */
export const handOffScriptComposerToChatOperation = createLuminaryOperation(
    async (
        { getState, call },
        { scriptLines, currentAIVoiceId, scriptWordCount }: ToolboxHandoffArgs,
    ) => {
        const state = getState();
        const videoId = getVideoIdOrThrow(state);
        const videoInitType = getVideoInitType(state);
        const referenceMaterial = getReferenceMaterial(state);
        const documentName = selectDocumentName(state);
        const selectedTemplateId = getSelectedTemplateId(state);
        const selectedPaletteIndex = getSelectedPaletteIndex(state);
        const selectedPalette =
            selectedTemplateId && selectedPaletteIndex !== null
                ? (getTemplateColorPalettes(state, selectedTemplateId)?.[selectedPaletteIndex] ??
                  null)
                : null;
        const brandKitId = getBrandKitId(state);
        const brandKit = brandKitId !== null ? getBrandKitById(state, brandKitId) : null;
        const selectedBlueprint = getBlueprint(state, getSelectedBlueprintId(state));
        const currentAIVoice = currentAIVoiceId ? getAIVoiceById(state, currentAIVoiceId) : null;
        const currentAIAvatar =
            videoInitType === VIDEO_INIT_TYPE.TEXT_TO_AI_AVATAR ? getCurrentAIAvatar(state) : null;

        // An uploaded source doc travels as a chat attachment (its extracted
        // text included), so the message skips the reference-material block.
        const sourceDocument: SerializedDocument | null =
            getDocToScriptImportType(state) === SCRIPT_CREATION_SOURCES.DOCUMENT && documentName
                ? {
                      id: uuid(),
                      name: documentName,
                      type: inferDocumentFileType(documentName),
                      url: selectDocumentUrl(state) ?? '',
                      text: referenceMaterial,
                  }
                : null;

        const messageText = buildToolboxHandoffMessage({
            scriptLines,
            videoInitType,
            language: getEditedScriptLanguage(state),
            voice:
                currentAIVoiceId && currentAIVoice
                    ? { slug: currentAIVoiceId, name: currentAIVoice.display_name }
                    : null,
            avatar: currentAIAvatar ? { id: currentAIAvatar.id, name: currentAIAvatar.name } : null,
            templateId: selectedTemplateId,
            aspectRatio: getSelectedAspectRatio(state),
            brandKit:
                brandKitId !== null && brandKit ? { id: brandKitId, name: brandKit.name } : null,
            palette: selectedPalette
                ? {
                      name: selectedPalette.name,
                      colors: getPaletteColorsAsHex(selectedPalette),
                  }
                : null,
            blueprint: selectedBlueprint
                ? { id: selectedBlueprint.id, name: selectedBlueprint.name }
                : null,
            enforceBlockSegmentation: getEnforceBlockSegmentation(state),
            referenceMaterial: sourceDocument ? null : referenceMaterial,
            hasAttachedSourceDocument: !!sourceDocument,
        });

        const media = await collectComposerImages(selectArticleId(state));

        const staged = saveDashboardSketchData({
            messageText,
            documents: sourceDocument ? [sourceDocument] : [],
            media,
            // The message carries the composer's own settings; the controls bar's
            // saved picks were never shown in the composer.
            fromScriptComposer: true,
        });
        if (!staged) {
            Toast.error('Unable to start building your video. Please try again.');
            return;
        }

        analytics.track('Script Composer handed off to Toolbox chat', {
            videoId,
            videoType: videoInitType,
            voice: currentAIVoiceId,
            script_word_count: scriptWordCount,
            image_count: media.length,
            has_source_document: !!sourceDocument,
        });

        await call(parkVideoAndEnterChatOperation);
    },
);
